"use client";
import React from "react";
import { X, Image as ImageIcon } from "lucide-react";
import { useImagePicker } from "@/hooks/useFilePicker";
import { FileSystemItem } from "@/types/fileManager";
import { generateImageSrc } from "@/utils/helper";

interface ImageUploadProps {
  pageId: string;
  currentImage?: string;
  isUploading: boolean;
  onImageSelected: (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => void;
  onRemoveImage: (pageId: string) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  pageId,
  currentImage,
  isUploading,
  onImageSelected,
  onRemoveImage,
}) => {
  // Use a demo store ID - in production, this should come from context/auth

  const imagePicker = useImagePicker(false); // single selection

  const handleSelectImage = () => {
    imagePicker.pickImages((files: FileSystemItem[]) => {
      if (files.length > 0) {
        const file = files[0];
        console.log("🔍 Selected file from picker - complete asset data:", {
          assetId: file.assetId,
          name: file.name,
          size: file.size,
          mimeType: file.mimeType,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          // All asset data is available immediately - no refetching needed!
        });

        // Generate proper URL from the file path using the helper
        const imageUrl = file.url || generateImageSrc(file.path);
        const selectedFile = {
          assetId: file.assetId,
          url: imageUrl,
        };
        console.log("📤 Sending to onImageSelected:", selectedFile);
        onImageSelected(pageId, selectedFile);
      }
    });
  };

  if (currentImage) {
    return (
      <div className="space-y-3">
        {/* Image Preview - Clickable */}
        <div
          className="relative w-full max-w-sm cursor-pointer group"
          onClick={handleSelectImage}
        >
          <img
            src={currentImage}
            alt="Featured image preview"
            className="w-full h-32 object-cover rounded-lg border border-border transition-opacity group-hover:opacity-75"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveImage(pageId);
            }}
            className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/80 transition-colors opacity-0 group-hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Hover overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
            <div className="bg-white/90 dark:bg-black/90 px-3 py-2 rounded-lg">
              <span className="text-sm font-medium text-foreground">
                Click to change image
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleSelectImage}
        disabled={isUploading}
        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer transition-colors hover:bg-muted ${
          isUploading ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          {isUploading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Processing...</p>
            </div>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground" />
              <p className="mb-1 text-sm text-muted-foreground">
                <span className="font-semibold">Click to select</span> featured
                image
              </p>
              <p className="text-xs text-muted-foreground">
                From your asset library or upload new
              </p>
            </>
          )}
        </div>
      </button>
    </div>
  );
};
